/**
 * Rutas canónicas Fase 3E — separación inicial /configuracion dual.
 * Alias legacy: CONFIGURACION_ALIAS_PATH → redirige según workspace/rol.
 */

/** Licencia, perfil fiscal y usuarios del espacio comprador (Configuracion.jsx). */
export const CONFIGURACION_DESPACHO_PATH = "/despacho/configuracion";
/** Parámetros mock del módulo Despacho (Fase 1); no confundir con licencia real. */
export const CONFIGURACION_DESPACHO_MODULO_MOCK_PATH = "/despacho/configuracion-modulo";
export const CONFIGURACION_EMISOR_PATH = "/emisor/licencias";
export const CONFIGURACION_ALIAS_PATH = "/configuracion";
export const LEGAL_DOCUMENTOS_PATH = "/legal/documentos";

/** Rutas de configuración (hub/admin — acceso sin guard de módulo activo). */
export const CONFIGURACION_HUB_PATHS = new Set([
  CONFIGURACION_ALIAS_PATH,
  CONFIGURACION_DESPACHO_PATH,
  CONFIGURACION_EMISOR_PATH,
]);

/**
 * Destino del alias temporal /configuracion (sin query string).
 * @param {{ mode?: string, canGenerateLicenses?: boolean, isPlatformDesigner?: boolean }} ctx
 */
export function resolveConfiguracionAliasTarget(ctx = {}) {
  const mode = String(ctx.mode || "despacho").toLowerCase();
  const canEmit = Boolean(ctx.canGenerateLicenses);
  const isDesigner = Boolean(ctx.isPlatformDesigner);

  if (isDesigner || (canEmit && mode === "distribuidor")) {
    return CONFIGURACION_EMISOR_PATH;
  }
  return CONFIGURACION_DESPACHO_PATH;
}

/**
 * @param {string} pathname
 */
export function isConfiguracionHubPath(pathname) {
  const path = String(pathname || "").split("?")[0];
  return CONFIGURACION_HUB_PATHS.has(path);
}

/**
 * URL de Configuracion.jsx (licencia/perfil) con deep links de activación.
 * @param {{ solicitarLicencia?: boolean, activarLicencia?: boolean }} opts
 */
export function buildConfiguracionLicenciaUrl(opts = {}) {
  const params = new URLSearchParams();
  if (opts.solicitarLicencia) params.set("solicitar_licencia", "1");
  if (opts.activarLicencia) params.set("activar_licencia", "1");
  const qs = params.toString();
  return qs ? `${CONFIGURACION_DESPACHO_PATH}?${qs}` : CONFIGURACION_DESPACHO_PATH;
}

/**
 * ¿Query de vista enfocada de solicitud comercial (?solicitar_licencia=1 | ?tab=licencia)?
 * @param {URLSearchParams|object} searchParams
 */
export function isLicenseCommercialFocusQuery(searchParams) {
  const get = (key) => {
    if (searchParams && typeof searchParams.get === "function") {
      return searchParams.get(key);
    }
    return searchParams?.[key];
  };
  return get("solicitar_licencia") === "1" || get("tab") === "licencia";
}

/** Valor del acordeón «Mi Negocio / Licencia» en Configuracion.jsx. */
export const CONFIGURACION_SISTEMA_SECTION = "sistema";

export const LICENSE_RENEWAL_EXPANSION_QUERY = "renovar_ampliar";
export const LICENSE_REQUEST_HISTORY_QUERY = "ver_historial_licencia";

/**
 * Deep link a Mi Negocio → renovación/ampliación (formulario completo).
 * @param {{ viewHistory?: boolean, expansionType?: string, moduleId?: string }} opts
 */
export function buildConfiguracionRenovacionUrl(opts = {}) {
  const params = new URLSearchParams();
  if (opts.viewHistory) {
    params.set(LICENSE_REQUEST_HISTORY_QUERY, "1");
  } else {
    params.set(LICENSE_RENEWAL_EXPANSION_QUERY, "1");
  }
  if (opts.expansionType) params.set("expansion_type", opts.expansionType);
  if (opts.moduleId) params.set("expansion_module", opts.moduleId);
  const qs = params.toString();
  return qs ? `${CONFIGURACION_DESPACHO_PATH}?${qs}` : CONFIGURACION_DESPACHO_PATH;
}

/**
 * @param {URLSearchParams|object} searchParams
 */
export function parseRenovacionAmpliacionPrefill(searchParams) {
  const get = (key) => {
    if (searchParams && typeof searchParams.get === "function") {
      return searchParams.get(key);
    }
    return searchParams?.[key];
  };
  const scrollToRenewal = get(LICENSE_RENEWAL_EXPANSION_QUERY) === "1";
  const openHistory = get(LICENSE_REQUEST_HISTORY_QUERY) === "1";
  const expansionType = String(get("expansion_type") || "").trim();
  const moduleId = String(get("expansion_module") || "").trim();

  let prefill = null;
  if (scrollToRenewal && (expansionType || moduleId)) {
    prefill = {
      expansion_type: expansionType || "module_upgrade",
      requested_modules: moduleId ? { [moduleId]: true } : {},
    };
  }

  return { scrollToRenewal, openHistory, prefill };
}

/**
 * ¿Abrir sección licencia por deep link (?solicitar_licencia=1 | ?activar_licencia=1)?
 * @param {URLSearchParams|{ solicitar_licencia?: string, activar_licencia?: string }} searchParams
 */
export function shouldOpenLicenciaSectionFromQuery(searchParams) {
  const get = (key) => {
    if (searchParams && typeof searchParams.get === "function") {
      return searchParams.get(key);
    }
    return searchParams?.[key];
  };
  return (
    get("solicitar_licencia") === "1"
    || get("activar_licencia") === "1"
    || get(LICENSE_RENEWAL_EXPANSION_QUERY) === "1"
    || get(LICENSE_REQUEST_HISTORY_QUERY) === "1"
  );
}


/**
 * Fusiona sección licencia en openSections si el query lo pide.
 * @param {string[]} openSections
 * @param {URLSearchParams|object} searchParams
 */
export function mergeLicenciaDeepLinkOpenSections(openSections = [], searchParams) {
  if (!shouldOpenLicenciaSectionFromQuery(searchParams)) {
    return openSections;
  }
  if (openSections.includes(CONFIGURACION_SISTEMA_SECTION)) {
    return openSections;
  }
  return [...openSections, CONFIGURACION_SISTEMA_SECTION];
}
